'use client';

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  checkUserAuthStatus,
  getCurrentUser,
  getCurrentUserMemberships,
  loginUser,
  logoutUser,
} from '@cloudquery/api-cloud';
import toast from 'react-hot-toast';

import { useUpdateEffect } from 'shared/base';

import { BaseAuthContext, BaseAuthContextType, initialAuthState, logoutAuthState } from './context';

import { TURNSTILE_TOKEN_LOCALSTORAGE_KEY } from './turnstileContext';
import { BaseAuthMembership } from './types';
import {
  sortMemberships,
  mergeMemberships,
  getCurrentMembership,
  getSamlEndpoint,
  getMfaRequired,
  getMfaConfigured,
} from './utils';

import { saveUserSelectedTeam } from '../../utils/user';

interface Props {
  checkAuthStatus?: boolean;
  children: ReactNode;
  pathname?: string;
  routeQueryTeamName?: string;
  skipMembershipCheck?: boolean;
}

const NOT_AUTHENTICATED_LOCALSTORAGE_KEY = '_cloudquery_platform_user_not_authenticated';

export function BaseAuthProvider({
  checkAuthStatus,
  children,
  pathname,
  routeQueryTeamName,
  skipMembershipCheck = false,
}: Props) {
  const loggedInRef = useRef(false);
  const manuallySignedOutRef = useRef(false);
  const [state, setState] = useState<BaseAuthContextType>(initialAuthState);
  const routeQueryTeamNameRef = useRef(routeQueryTeamName);
  routeQueryTeamNameRef.current = routeQueryTeamName;

  const setCurrentMembership = useCallback(
    (membership: BaseAuthMembership, updateRecord = true): void => {
      setState((state) => ({
        ...state,
        currentMembership: membership,
        memberships: updateRecord
          ? sortMemberships(mergeMemberships(state.memberships, membership))
          : state.memberships,
      }));
      saveUserSelectedTeam(membership.team.name);
    },
    [],
  );

  const checkLoginStatus = useCallback(async () => {
    if (checkAuthStatus) {
      const { data: authStatus } = await checkUserAuthStatus();
      if (!authStatus.authenticated) {
        throw new Error('User is not authenticated');
      }
    }

    const userFetchResponse = await getCurrentUser().catch((error: any) => {
      if ('status' in error && error.status === 401) {
        localStorage.setItem(NOT_AUTHENTICATED_LOCALSTORAGE_KEY, 'true');
      }

      setState((state) => ({
        ...state,
        samlEndpoint: getSamlEndpoint(error.headers),
        userMfaConfigured: getMfaConfigured(error.headers),
        userMfaRequired: getMfaRequired(error.headers),
      }));

      throw error;
    });

    const samlEndpoint = getSamlEndpoint(userFetchResponse?.headers);
    const membershipFetchResponse = skipMembershipCheck
      ? { data: { items: [] } }
      : await getCurrentUserMemberships();
    const memberships = membershipFetchResponse?.data?.items || [];
    const currentMembership = getCurrentMembership(memberships, routeQueryTeamNameRef.current);

    if (currentMembership && currentMembership.team.name) {
      saveUserSelectedTeam(currentMembership.team.name);
    }

    if (localStorage.getItem(NOT_AUTHENTICATED_LOCALSTORAGE_KEY) === 'true') {
      localStorage.removeItem(NOT_AUTHENTICATED_LOCALSTORAGE_KEY);
      loggedInRef.current = true;
    }

    setState((state) => ({
      ...state,
      currentMembership: currentMembership || null,
      isAuthenticated: true,
      isInitialized: true,
      memberships: sortMemberships(membershipFetchResponse.data.items || []),
      samlEndpoint,
      user: {
        avatar: userFetchResponse!.data?.profile_image_url,
        created_at: userFetchResponse!.data?.created_at,
        ...userFetchResponse!.data,
      },
      userMfaConfigured: getMfaConfigured(userFetchResponse!.headers),
      userMfaRequired: getMfaRequired(userFetchResponse!.headers),
    }));
  }, [checkAuthStatus, skipMembershipCheck]);

  const refresh = useCallback(async () => {
    return checkLoginStatus().catch((error) => {
      setState((state) => ({
        ...state,
        ...logoutAuthState,
      }));

      throw error;
    });
  }, [checkLoginStatus]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await logoutUser();
      manuallySignedOutRef.current = true;
      localStorage.setItem(NOT_AUTHENTICATED_LOCALSTORAGE_KEY, 'true');
      localStorage.removeItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY);

      setState((state) => ({
        ...state,
        ...logoutAuthState,
      }));
    } catch {
      toast.error('Something went wrong when logging out. Please try again later');
    }

    await refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (token: string) => {
      setState((state) => ({ ...state, error: null, isLoading: true }));
      try {
        await loginUser({
          id_token: token,
        });
        await checkLoginStatus();
      } catch (error) {
        setState((state) => ({ ...state, isLoading: false }));
        throw error;
      }
    },
    [checkLoginStatus],
  );

  // Check if the user is authenticated when the page is visible
  useEffect(() => {
    const checkAuth = async () => {
      if (document.visibilityState === 'visible') {
        checkLoginStatus().catch(() => {
          setState((state) => ({
            ...state,
            ...logoutAuthState,
          }));
        });
      }
    };
    document.addEventListener('visibilitychange', checkAuth);

    return () => {
      document.removeEventListener('visibilitychange', checkAuth);
    };
  }, [signOut, checkLoginStatus]);

  // Update current membership and save team name to local storage if it has changed
  useUpdateEffect(() => {
    const currentMembership = getCurrentMembership(state.memberships, routeQueryTeamName);

    if (
      currentMembership?.team?.name &&
      currentMembership.team.name === state.currentMembership?.team?.name
    ) {
      return;
    }

    setState((state) => ({ ...state, currentMembership }));

    if (currentMembership) {
      saveUserSelectedTeam(currentMembership.team.name);
    }
  }, [routeQueryTeamName]);

  useUpdateEffect(() => {
    setState((state) => ({ ...state, error: null }));
  }, [pathname]);

  const contextValue = useMemo<BaseAuthContextType>(
    () => ({
      ...state,
      loggedInRef,
      manuallySignedOutRef,
      refresh,
      setCurrentMembership,
      setError: (error) => setState((state) => ({ ...state, error })),
      setIsLoading: (value) => setState((state) => ({ ...state, isLoading: value })),
      setLogoutState: () => {
        localStorage.removeItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY);
        setState((state) => ({ ...state, ...logoutAuthState }));
      },
      setProvider: (provider) => setState((state) => ({ ...state, provider })),
      setRedirectSuccess: (value) => setState((state) => ({ ...state, redirectSuccess: value })),
      signIn,
      signOut,
    }),
    [setCurrentMembership, signOut, state, signIn, refresh],
  );

  return <BaseAuthContext.Provider value={contextValue}>{children}</BaseAuthContext.Provider>;
}
